/**
 * Sandbox harness for slice-4 card combat (`?sandbox=cards`). A menu launches
 * either the symmetric combat overlay or the out-of-combat inventory editor.
 * Drives the real `cardEngine` (ADR-0015 §11) against the authored V1 pool
 * (HEX-51) and a common-tier enemy template (Goblin Striker).
 *
 * The player begins with a varied hand packed onto the single 3×3 loadout — the
 * only card store (ADR-0022; stash retired) — mixing attack / defense / support
 * / recursive / trap / die so the inventory editor has every type to demo. The
 * genesis flow proper lives outside this harness; this is a manual drive for the
 * loop, not the production path.
 */

import { applyIntent, createCombat } from '../combat/cardEngine';
import { POWER_STRIKE } from '../combat/cards/attack/power-strike';
import { BRACE } from '../combat/cards/defense/brace';
import { EXTRA_DIE } from '../combat/cards/die/extra-die';
import { SPARK } from '../combat/cards/recursive/spark';
import { REROLL } from '../combat/cards/support/reroll';
import { CALTROPS } from '../combat/cards/trap/caltrops';
import { createRng } from '../combat/dice';
import { HEAVY_DIE, dieCard } from '../combat/diceRoster';
import { GOBLIN_STRIKER } from '../combat/enemies/goblin-striker';
import { type EnemyTemplate, buildEnemy } from '../combat/enemies/template';
import { TOWER_SHIELD_GUARD } from '../combat/enemies/tower-shield-guard';
import { createStatusBag } from '../combat/status';
import type { Card, Combatant, GridPlacement, Inventory } from '../combat/types';
import { CardCombatOverlay, type CombatOutcome } from '../combat/ui/CardCombatOverlay';
import { InventoryEditor } from '../combat/ui/InventoryEditor';
import { LevelUpOverlay } from '../combat/ui/LevelUpOverlay';
import { addCard } from '../combat/ui/inventoryEdit';
import { rollLevelUpOffers } from '../progression/xp';

import '../combat/ui/card-hover-panel-styles.css';
import '../combat/ui/card-combat-overlay-styles.css';
import '../combat/ui/inventory-editor-styles.css';

function place(card: Card, row: number, col: number, rotated = false): GridPlacement {
  return { card, origin: { row, col }, rotated };
}

/** ADR-0015 §5 starter pair plus a spare of each interesting type, all packed
 * onto the single 3×3 loadout (ADR-0022) for hand-demo via the inventory editor. */
function makeStarterPlayer(): Combatant {
  return {
    id: 'wanderer',
    name: 'Wanderer',
    hp: 20,
    maxHp: 20,
    inventory: makeStarterInventory(),
    dicePool: [],
    basePoolSize: 3,
    statusBag: createStatusBag(),
    block: 0,
    trapState: new Map(),
    frozenCards: new Map(),
    oncePerCombatFired: new Set(),
    oncePerTurnFired: new Set(),
  };
}

function makeStarterInventory(): Inventory {
  return {
    // A varied hand packed onto the single 3×3 loadout (ADR-0022), one of each
    // type, filling all 9 cells: Power Strike 2×1 @ (0,0), Reroll 1×1 @ (0,2),
    // Brace 2×1 @ (1,0), Extra Die 1×1 @ (1,2), Caltrops 2×1 @ (2,0), Spark 1×1
    // @ (2,2).
    loadout: {
      placements: [
        place(POWER_STRIKE, 0, 0),
        place(REROLL, 0, 2),
        place(BRACE, 1, 0),
        place(EXTRA_DIE, 1, 2),
        place(CALTROPS, 2, 0),
        place(SPARK, 2, 2),
      ],
      capacity: { rows: 3, cols: 3 },
    },
  };
}

export class CardSandbox {
  private readonly root: HTMLElement;
  private overlay: CardCombatOverlay | null = null;
  private editor: InventoryEditor | null = null;
  private levelUpOverlay: LevelUpOverlay | null = null;
  private mountPoint: HTMLDivElement | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
  }

  mount(): void {
    this.renderMenu();
  }

  destroy(): void {
    this.teardown();
    this.root.innerHTML = '';
  }

  private renderMenu(): void {
    this.teardown();
    this.root.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;padding:24px;';

    const title = document.createElement('h1');
    title.textContent = 'Card combat sandbox';
    title.style.cssText = 'margin:0;font-size:26px;letter-spacing:0.12em;';
    const sub = document.createElement('p');
    sub.textContent = 'ADR-0015 §12 symmetric combat · out-of-combat inventory editor';
    sub.style.cssText = 'margin:0;opacity:0.7;font-size:13px;';

    const seedRow = document.createElement('label');
    seedRow.style.cssText = 'display:flex;gap:8px;align-items:center;font-size:13px;';
    seedRow.textContent = 'Seed ';
    const seed = document.createElement('input');
    seed.type = 'text';
    seed.value = '7';
    seed.dataset.role = 'seed-input';
    seed.style.cssText =
      'padding:6px 10px;background:#0c0c10;color:inherit;border:1px solid #2a2a36;border-radius:6px;width:120px;';
    seedRow.appendChild(seed);

    const buttons = document.createElement('div');
    buttons.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center;';
    buttons.append(
      this.menuButton('Combat: Goblin (common)', 'start-combat-goblin', () =>
        this.startCombat(GOBLIN_STRIKER, parseSeed(seed.value)),
      ),
      this.menuButton('Combat: Tower Guard (rare)', 'start-combat-tower', () =>
        this.startCombat(TOWER_SHIELD_GUARD, parseSeed(seed.value)),
      ),
      this.menuButton('Inventory', 'start-inventory', () => this.startInventory()),
      this.menuButton('Level Up overlay', 'start-level-up', () =>
        this.startLevelUp(parseSeed(seed.value)),
      ),
    );

    wrap.append(title, sub, seedRow, buttons);
    this.root.appendChild(wrap);
  }

  private menuButton(label: string, action: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.dataset.action = action;
    btn.style.cssText =
      'padding:12px 20px;font-size:15px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';
    btn.addEventListener('click', onClick);
    return btn;
  }

  private fullScreenMount(): HTMLDivElement {
    this.root.innerHTML = '';
    const mount = document.createElement('div');
    mount.dataset.role = 'screen-mount';
    mount.style.cssText = 'position:relative;flex:1;min-height:0;';
    this.root.appendChild(mount);
    this.mountPoint = mount;
    return mount;
  }

  private startCombat(template: EnemyTemplate, seed: number): void {
    const mount = this.fullScreenMount();
    const overlay = new CardCombatOverlay();
    overlay.open(
      mount,
      {
        player: makeStarterPlayer(),
        enemy: buildEnemy(template),
        seed,
        enemyTier: template.tier,
        enemyGlyph: template.glyph,
      },
      { createCombat, applyIntent },
      (outcome) => this.showOutcome(outcome),
    );
    this.overlay = overlay;
  }

  private startLevelUp(seed: number): void {
    const mount = this.fullScreenMount();
    const offers = rollLevelUpOffers(createRng(seed));
    const overlay = new LevelUpOverlay(mount);
    this.levelUpOverlay = overlay;
    overlay.start({
      level: 2,
      offers,
      onPick: async (offer) => {
        // Sandbox: no real character to mutate — just log.
        const label = offer.kind === 'card' ? offer.card.name : offer.die.name;
        console.info(`[sandbox] level-up pick: ${offer.kind} — ${label}`);
      },
      onDone: () => this.renderMenu(),
    });
  }

  private startInventory(): void {
    const mount = this.fullScreenMount();
    // Pack a sample specialist die so the in-grid die-chip is exercised (ADR-0024 §5).
    const base = makeStarterInventory();
    const withDie = addCard(base, dieCard(HEAVY_DIE));
    const inventory = withDie?.inventory ?? base;
    const editor = new InventoryEditor(inventory);
    editor.mount(mount, {
      inventory,
      onClose: () => this.renderMenu(),
    });
    this.editor = editor;
  }

  private showOutcome(outcome: CombatOutcome): void {
    if (!this.mountPoint) return;
    const bar = document.createElement('div');
    bar.dataset.role = 'outcome-bar';
    bar.style.cssText =
      'position:absolute;bottom:18px;left:50%;transform:translateX(-50%);z-index:40;display:flex;gap:8px;align-items:center;background:#15151b;border:1px solid #34344a;border-radius:8px;padding:8px 14px;';
    const label = document.createElement('span');
    label.textContent = `Outcome: ${outcome}`;
    label.style.cssText = 'font-size:13px;letter-spacing:0.08em;';
    const back = document.createElement('button');
    back.type = 'button';
    back.textContent = 'Menu';
    back.dataset.action = 'back-to-menu';
    back.style.cssText =
      'padding:8px 14px;font-size:13px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';
    back.addEventListener('click', () => this.renderMenu());
    bar.append(label, back);
    this.mountPoint.appendChild(bar);
  }

  private teardown(): void {
    this.overlay?.destroy();
    this.overlay = null;
    this.editor?.destroy();
    this.editor = null;
    this.levelUpOverlay?.destroy();
    this.levelUpOverlay = null;
    this.mountPoint = null;
  }
}

function parseSeed(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return Date.now();
  const n = Number(trimmed);
  if (Number.isFinite(n)) return Math.floor(n);
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) hash = ((hash << 5) - hash + trimmed.charCodeAt(i)) | 0;
  return hash;
}
